import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  isDecimalMode = false;
  decimalMultiplier = 10;
  boundFormSubmitHandler = null;
  boundFormDataHandler = null;

  connectedCallback() {
    super.connectedCallback();

    // Check if this is a decimal mode selector
    this.isDecimalMode = this.hasAttribute('data-decimal-mode');
    if (this.isDecimalMode) {
      this.decimalMultiplier = parseInt(this.getAttribute('data-decimal-multiplier')) || 10;
      this.initializeDecimalMode();
      
      // Add form handlers
      const form = this.closest('form');
      if (form) {
        // Handle formdata event to modify FormData object
        this.boundFormDataHandler = (e) => {
          const displayValue = parseFloat(this.refs.quantityInput.value) || 0.1;
          const actualValue = Math.round(displayValue * this.decimalMultiplier);
          
          console.log('FORMDATA event - Converting:', displayValue, '→', actualValue);
          
          // Update the FormData object directly
          e.formData.set('quantity', actualValue.toString());
        };
        
        form.addEventListener('formdata', this.boundFormDataHandler, true);
        
        // Keep submit handler for display restoration
        this.boundFormSubmitHandler = this.handleFormSubmit.bind(this);
        form.addEventListener('submit', this.boundFormSubmitHandler, true);
      }
    }

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Remove form handlers
    if (this.isDecimalMode) {
      const form = this.closest('form');
      if (form) {
        if (this.boundFormSubmitHandler) {
          form.removeEventListener('submit', this.boundFormSubmitHandler, true);
        }
        if (this.boundFormDataHandler) {
          form.removeEventListener('formdata', this.boundFormDataHandler, true);
        }
      }
    }
  }

  /**
   * Initialize decimal mode display
   */
  initializeDecimalMode() {
    const { quantityInput } = this.refs;
    
    // Keep as type="number" but set step to 0.1
    quantityInput.setAttribute('step', '0.1');
    
    // For product pages, set min to 0.2 (display) but actual min is 2
    // The HTML min="0.2" is for browser validation of display values
    // But we enforce actual min of 2 units in our logic
    quantityInput.setAttribute('min', '0.2');
    
    // Convert initial value to decimal display
    const actualValue = parseInt(quantityInput.value) || 2; // Default to 2 units (0.2 metres)
    const displayValue = (actualValue / this.decimalMultiplier).toFixed(1);
    quantityInput.value = displayValue;
    
    // Store the actual integer value
    quantityInput.setAttribute('data-actual-value', actualValue.toString());
  }

  /**
   * Handle form submission - just for logging and restoration
   */
  handleFormSubmit(event) {
    if (!this.isDecimalMode) return;
    
    const { quantityInput } = this.refs;
    const displayValue = parseFloat(quantityInput.value) || 0.1;
    
    console.log('FORM SUBMIT - Display value:', displayValue);
    
    // The formdata event handler already set the correct value in FormData
    // We just need to restore the display value after a moment
    setTimeout(() => {
      if (quantityInput.isConnected && this.isDecimalMode) {
        quantityInput.value = displayValue.toFixed(1);
        console.log('Restored display value:', quantityInput.value);
      }
    }, 50);
  }

  /**
   * Convert display value to actual stock units
   * @param {string} displayValue - The decimal value (e.g., "0.4")
   * @returns {number} The actual stock units (e.g., 4)
   */
  displayToActual(displayValue) {
    const decimal = parseFloat(displayValue) || 0.1;
    return Math.max(1, Math.round(decimal * this.decimalMultiplier));
  }

  /**
   * Convert actual stock units to display value
   * @param {number} actualValue - The stock units (e.g., 4)
   * @returns {string} The decimal value (e.g., "0.4")
   */
  actualToDisplay(actualValue) {
    return (actualValue / this.decimalMultiplier).toFixed(1);
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    if (this.isDecimalMode) {
      return this.refs.quantityInput.getAttribute('data-actual-value') || '1';
    }
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    if (this.isDecimalMode) {
      const actualValue = parseInt(value) || 1;
      this.refs.quantityInput.setAttribute('data-actual-value', actualValue.toString());
      this.refs.quantityInput.value = this.actualToDisplay(actualValue);
    } else {
      this.refs.quantityInput.value = value;
    }
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = this.isDecimalMode 
      ? parseInt(quantityInput.getAttribute('data-actual-value')) || 1
      : parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseInt(min) || 1;
    const newStep = parseInt(step) || 1;
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      this.setValue(newValue.toString());
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;
    const value = this.isDecimalMode
      ? parseInt(quantityInput.getAttribute('data-actual-value')) || 1
      : parseInt(quantityInput.value) || 0;
    
    // For decimal mode on product pages, actual minimum is 2 units (0.2 metres)
    const actualMin = this.isDecimalMode ? 2 : (parseInt(quantityInput.min) || 1);
    
    return {
      min: actualMin,
      max: quantityInput.max ? parseInt(quantityInput.max) : null,
      step: parseInt(quantityInput.step) || 1,
      value: value,
      cartQuantity: parseInt(quantityInput.getAttribute('data-cart-quantity') || '0'),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, value + step * stepMultiplier)
    );

    if (this.isDecimalMode) {
      quantityInput.setAttribute('data-actual-value', newValue.toString());
      quantityInput.value = this.actualToDisplay(newValue);
      this.updateUnitLabel(newValue); // Add this line
    } else {
      quantityInput.value = newValue.toString();
    }
    
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Updates the unit label (metre/metres) based on quantity
   * @param {number} actualValue - The actual stock units
   */
  updateUnitLabel(actualValue) {
    const unitLabel = this.querySelector('.quantity-unit-label');
    if (!unitLabel) return;
    
    // Show "metre" for exactly 10 units (1.0 metres), otherwise "metres"
    unitLabel.textContent = actualValue === 10 ? 'metre' : 'metres';
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let quantity;
    if (this.isDecimalMode) {
      // Parse decimal value and convert to actual units
      const displayValue = parseFloat(event.target.value) || 0.1;
      const roundedDisplay = Math.round(displayValue * 10) / 10;
      quantity = this.displayToActual(roundedDisplay.toString());
      
      // Update display
      quantityInput.value = roundedDisplay.toFixed(1);
    } else {
      quantity = parseInt(event.target.value) || 0;
    }

    // Snap to bounds
    quantity = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, quantity)
    );

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      if (this.isDecimalMode) {
        quantityInput.setAttribute('data-actual-value', quantity.toString());
        quantityInput.value = this.actualToDisplay(quantity);
      } else {
        quantityInput.value = quantity.toString();
      }
      quantityInput.reportValidity();
      return;
    }

    if (this.isDecimalMode) {
      quantityInput.setAttribute('data-actual-value', quantity.toString());
      quantityInput.value = this.actualToDisplay(quantity);
      this.updateUnitLabel(quantity); // Add this line
    } else {
      quantityInput.value = quantity.toString();
    }
    
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = this.isDecimalMode
      ? parseInt(quantityInput.getAttribute('data-actual-value')) || 1
      : parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, value)
    );

    if (clampedValue !== value) {
      if (this.isDecimalMode) {
        quantityInput.setAttribute('data-actual-value', clampedValue.toString());
        quantityInput.value = this.actualToDisplay(clampedValue);
      } else {
        quantityInput.value = clampedValue.toString();
      }
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}